<template>
  <DataTable
    v-model:multi-sort-meta="multiSortMeta"
    data-cy="sizings-table"
    sort-mode="multiple"
    :first="offset"
    :value="sizings"
    :total-records="sizingsAggregateCount"
    :rows="limit"
    :selection-mode="checkedRows ? 'multiple' : undefined"
    :selection="checkedRows"
    auto-layout
    lazy
    paginator
    paginator-position="bottom"
    paginator-template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
    responsive-layout="stack"
    current-page-report-template="Showing {first} to {last} of {totalRecords}"
    @update:first="$emit('update:offset', $event)"
    @update:limit="$emit('update:limit', $event)"
    @update:selection="$emit('update:checked-rows', $event)"
  >
    <template #empty>
      <p class="text-center">No sizings found.</p>
    </template>
    <Column
      v-if="!!checkedRows"
      selectionMode="multiple"
      headerStyle="width: 3rem"
    ></Column>
    <column field="title" header="Name" :sortable="true" data-cy="column-name">
      <template #body="{ data }">
        <router-link
          v-if="renderLinks"
          :to="{
            name: 'WellInfo',
            params: getRouteParamsFromSizing(data)
          }"
        >
          <Badge
            v-if="data.imported_sizing_id"
            severity="danger"
            class="p-overlay-badge"
          />
          <icon
            v-if="data.pumping_type === Sizing_Pumping_Type_Enum.Hps"
            :icon="['fas', 'rug']"
            class="hps"
          />
          {{ data.title || "Untitled" }}
        </router-link>
        <span v-else>
          {{ data.title || "Untitled" }}
        </span>
      </template>
    </column>
    <column
      v-if="!hideWell"
      field="well.well_name"
      header="Well"
      :sortable="true"
      data-cy="column-well"
    >
      <template #body="{ data }">
        <router-link
          v-if="data.well?.id && renderLinks"
          :to="{
            name: 'Well',
            params: { well: data.well.id }
          }"
        >
          {{ data.well.well_name }}
        </router-link>
        <span v-else-if="data.well?.id">
          {{ data.well.well_name }}
        </span>
      </template>
    </column>
    <column
      field="customer.customer_name"
      header="Customer"
      :sortable="true"
      data-cy="column-customer"
    />
    <column
      v-if="!hideOwner"
      field="author.display_name"
      header="Owner"
      :sortable="false"
    >
      <template #body="{ data }">
        {{ getAuthorName(data) }}
      </template>
    </column>
    <column field="parent_sizing" header="Parent Sizing">
      <template #body="{ data }">
        <router-link
          v-if="
            data.parent_sizing?.id && !data.parent_sizing?.journal_deleted_at
          "
          :to="{
            name: 'WellInfo',
            params: getRouteParamsFromSizing(data.parent_sizing)
          }"
          >{{ data.parent_sizing.title }}
        </router-link>
        <div
          v-else-if="
            data.parent_sizing?.id && data.parent_sizing?.journal_deleted_at
          "
        >
          <i>{{ data.parent_sizing?.title }}</i> (deleted)
        </div>
      </template>
    </column>
    <column field="journal_updated_at" header="Last Updated" :sortable="true">
      <template #body="{ data }">
        {{ new Date(data.journal_updated_at).toLocaleDateString() }}
      </template>
    </column>
    <template v-if="!checkedRows" #footer>
      <div class="legend" data-cy="table-footer">
        <Badge severity="danger" class="p-overlay-badge pt-2" />
        <span class="ml-2">Imported Sizing</span>
        <icon :icon="['fas', 'rug']" class="hps pl-2" />
        <span class="ml-2">HPS Sizing</span>
      </div>
    </template>
  </DataTable>
</template>
<script setup lang="ts">
import { Sizing_Pumping_Type_Enum } from "gql/api";
import type { SizingsWithScenarioIdsQuery } from "gql/api";
import { getRouteParamsFromSizing } from "@/utils/scenarioUtils";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import { type SortMeta } from "@/composables/primevue/tables";
import { ref, watch } from "vue";
const props = defineProps<{
  sort?: SortMeta[];
  sizings: SizingsWithScenarioIdsQuery["sizings"];
  sizingsAggregateCount: number;
  limit: number;
  offset: number;
  hideOwner?: boolean;
  hideWell?: boolean;
  checkedRows?: SizingsWithScenarioIdsQuery["sizings"];
  renderLinks?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:sort", value?: SortMeta[]): void;
  (e: "update:offset", value: number): void;
  (e: "update:limit", value: number): void;
  (
    e: "update:checked-rows",
    value: SizingsWithScenarioIdsQuery["sizings"]
  ): void;
}>();

const multiSortMeta = ref(props.sort);

watch(multiSortMeta, () => {
  emit("update:offset", 0);
  emit("update:sort", multiSortMeta.value);
});

const getAuthorName = (
  sizing: SizingsWithScenarioIdsQuery["sizings"][number]
): string => {
  if (sizing && sizing.journal_author_id === "import-user") {
    return sizing.author?.display_name || "Imported";
  }

  return sizing.author?.display_name ?? "";
};
</script>

<style lang="scss" scoped>
.legend {
  display: flex;
  align-items: center;
}
.p-badge {
  min-width: 0.5rem;
  height: 0.5rem;
  line-height: 0.5rem;
  font-size: 0.25rem;
}
.p-highlight a {
  color: white;
}
.hps {
  color: $spyglass-red;
}
</style>
